/**
 * Centralized access-token refresh.
 *
 * When an authenticated request returns 401 (the ~15-min access token expired
 * but the 7-day refresh token is still valid), `api-client.ts` calls
 * `refreshSession()` to obtain a fresh token pair and retry the original request
 * once. This keeps the user logged in instead of forcing a re-login.
 *
 * Design constraints:
 *  - **No import cycle.** This module talks to the auth-token layer and does a
 *    RAW `fetch` to `/auth/refresh` (it never imports `api-client.ts`, so the
 *    refresh call itself can never recurse into the refresh-on-401 logic).
 *  - **Single in-flight refresh.** Many requests can 401 at once; they all share
 *    one `refreshPromise` so only ONE `/auth/refresh` round-trip happens (no
 *    refresh storm, no refresh-token rotation conflict).
 *  - **Token rotation.** The backend rotates both tokens; we always store both.
 *  - **Logout wins.** If the session identity changes during the refresh (manual
 *    logout, or a fresh login), the completed refresh is discarded.
 *  - **Network ≠ logout.** A network/timeout failure while refreshing throws a
 *    `NetworkError` (not a 401), so a transient outage never logs the user out.
 *  Tokens are never logged.
 */

import { BASE_URL } from "./api-config";
import { NetworkError } from "./api-errors";
import { getAuthGeneration, getRefreshToken, setTokens } from "./auth-token";
import { timeStartup } from "./startup-perf";

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

const REFRESH_TIMEOUT_MS = 15_000;

let refreshPromise: Promise<string | null> | null = null;

/**
 * Returns a fresh access token, or `null` when refresh is impossible (no refresh
 * token, or the server rejected the refresh token with a 4xx). Throws
 * `NetworkError` when the refresh call cannot reach the server — callers must
 * treat that as "try again later", NOT as an expired session.
 *
 * Concurrent callers share a single in-flight refresh.
 */
export function refreshSession(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  // timeStartup records the round-trip duration in development only, and only
  // the phase name + milliseconds — never the token or the Authorization header.
  refreshPromise = timeStartup("token-refresh", doRefresh).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function doRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  // Snapshot the session identity so we can detect a logout/login mid-refresh.
  const generation = getAuthGeneration();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      signal: controller.signal,
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Unreachable / aborted: do NOT clear the session — surface as network.
    throw new NetworkError("API injoignable");
  } finally {
    clearTimeout(timer);
  }

  // 401 (invalid/expired/suspended) or any other non-2xx → refresh is impossible.
  if (!res.ok) return null;

  let data: RefreshResponse | null = null;
  try {
    data = (await res.json()) as RefreshResponse;
  } catch {
    return null;
  }
  if (!data?.accessToken || !data?.refreshToken) return null;

  // Manual-logout-wins: a logout (or a new login) happened while we were
  // refreshing → the freshly-minted pair belongs to a session that no longer
  // exists. Discard it so the completed refresh can never re-authenticate.
  if (getAuthGeneration() !== generation) return null;

  // Store BOTH rotated tokens; never keep the old refresh token.
  setTokens(data.accessToken, data.refreshToken);
  return data.accessToken;
}
